import type {
  Offer,
  OfferStatus,
  PromoCode,
} from '../../components/offers/data/types';

export type Deferred<T = void> = {
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (e: Error) => void;
};

export type FetchOptions = { force?: boolean; skipDuplicateMs?: number };

export interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

// ── Slice states ──

export interface OffersSummary {
  totalActiveOffersCount: number;
  totalRedemptions: number;
  totalDiscountAmount: number;
  totalOrderAmount: number;
}

export const emptyOffersSummary: OffersSummary = {
  totalActiveOffersCount: 0,
  totalRedemptions: 0,
  totalDiscountAmount: 0,
  totalOrderAmount: 0,
};

export interface OffersSliceState {
  offers: Offer[];
  summary: OffersSummary;
  loading: boolean;
  saving: boolean;
  error: string | null;
  loadedLocationId: string | null;
  editingOfferId: string | null;
}

export interface PromoCodesSliceState {
  promoCodes: PromoCode[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  editingPromoCodeId: string | null;
}

export interface ToastSliceState {
  toasts: ToastItem[];
}

// ── Root state ──

export interface OfferManagementState {
  offers: OffersSliceState;
  promoCodes: PromoCodesSliceState;
  toast: ToastSliceState;
}

// ── API payload types ──

export interface CreateOfferPayload {
  locationId: string;
  data: Omit<Offer, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'redemptionCount'>;
}

export interface UpdateOfferPayload {
  offerId: string;
  locationId: string;
  data: Partial<Omit<Offer, 'id' | 'createdAt' | 'updatedAt' | 'version' | 'redemptionCount'>>;
}

export interface ToggleOfferStatusPayload {
  offerId: string;
  locationId: string;
  status: OfferStatus;
}

export interface BulkArchiveOffersPayload {
  offerIds: string[];
}

export interface BulkDeleteOffersPayload {
  offerIds: string[];
  locationId: string;
}

export interface CreatePromoCodePayload {
  offerId: string;
  locationId: string;
  data: Omit<PromoCode, 'id' | 'createdAt' | 'usageCount'>;
}

export interface UpdatePromoCodePayload {
  promoCodeId: string;
  offerId: string;
  locationId: string;
  data: Partial<Omit<PromoCode, 'id' | 'createdAt' | 'offerId' | 'usageCount'>>;
}
